/************************************************
 * Client Specific Route Settings
 ************************************************/
// @mui icons
import Icon from "@mui/material/Icon";
import HomeIcon from "@mui/icons-material/Home";

 // Material Dashboard 2 PRO React components
import MDAvatar from "components/MDAvatar";
// import Analytics from "layouts/dashboards/analytics";

// Common page components
import HomePage from "pages/common/home";

// User Profile components
import UserProfile from "pages/common/account/profile";
import InitialsAvatar from "pages/common/account/profile/components/InitialsAvatar";

/************************************************
 * CLIENT ROUTE IMPORTS
 * GRM uses legacy routes
 * RDX routes available as APP_ADMIN fallback
 ************************************************/
import grmRoutes from 'clients/grm/grmRoutes'
import rdxRoutes from 'clients/rdx/rdxRoutes'

/************************************************
 * NEW CLIENT:  Import icon for new client
 ************************************************/
import afa92Icon from 'assets/images/afa92b-icon.png'
import demIcon from 'assets/images/dem/dem-icon.png'
import grmIcon from 'assets/images/grimaldis-icon.png'
import rdxIcon from 'assets/images/rd-logo-white.png'
// import divider from "./assets/theme/components/divider";

import Dashboard from 'clients/rdx/views/Dashboard'

/************************************************
 * Enhanced Navigation System
 ************************************************/
import buildRoutesFromNavigation, { hasValidNavigation } from 'utils/buildRoutesFromNavigation'

/**
 * Get common routes available to all authenticated users
 * These routes appear at the top of the navigation for all clients
 */
function getCommonRoutes() {
  return [
    {
      type: 'collapse',
      name: 'Home',
      key: 'home',
      route: '/home',
      icon: <HomeIcon fontSize='medium' />,
      component: <HomePage />,
      noCollapse: true,
    },
  ];
}

export default function setUserRoutes(user, permissions) {
  console.log('setUserRoutes')
  console.log('permissions object:', permissions)

  let client = user.client
  let role = user.role
  let firstName = user.firstName
  let lastName = user.lastName
  let displayName = user.username
  if(firstName){
    displayName = `${firstName} ${lastName}`
  }

  let routes = []
  let icon

  /**********************************************
   * NEW CLIENT:  Add case for selecting client
   * icon.
   **********************************************/
  switch(client) {
    case 'afa92':
      icon = afa92Icon
      break;
    case 'dem':
      // Demo
      icon = demIcon
      break;
    case 'grm':
      // Grimaldi's
      icon = grmIcon
      break;
    case 'rdx':
      // RadixDash
      icon = rdxIcon
      break;
    default:
      icon = rdxIcon
      // Use empty routes
  }

  /**********************************************
   * PERMISSION SYSTEM ROUTING
   * GRM: Uses legacy listUserPermissions system
   * All others: Use enhanced navigation system
   **********************************************/

  // GRM uses legacy permission system
  if (client === 'grm') {
    console.log('Using legacy permission system for GRM client');

    // Build routes with user profile first, then Home, then client-specific
    routes = [
      {
        type: 'collapse',
        name: displayName,
        key: displayName,
        route: '/user/profile',
        icon: <InitialsAvatar user={user} size='sm' />,
        component: <UserProfile />,
        noCollapse: true,
      },
      ...getCommonRoutes(),
      { type: 'divider', key: 'divider-1' },
    ];

    // Add GRM-specific routes if permissions available
    if (permissions !== null) {
      const clientRoutes = grmRoutes(displayName, role, permissions);
      routes = [...routes, ...clientRoutes];
    }

    return routes;
  }

  /**********************************************
   * ENHANCED NAVIGATION SYSTEM (All non-GRM clients)
   * Requires navigation.version === 5
   * NO fallback - if navigation fails, show error
   **********************************************/
  console.log('Using enhanced navigation system');

  // Check if navigation is valid
  if (hasValidNavigation(permissions)) {
    console.log('Valid navigation structure detected, building routes...');
    const navRoutes = buildRoutesFromNavigation(permissions.navigation, user, icon);

    // Insert Home after user profile (first route from buildRoutesFromNavigation is usually identity)
    // If navRoutes has routes, insert Home after the first one (identity), otherwise prepend
    if (navRoutes.length > 0) {
      routes = [
        navRoutes[0], // User profile/identity
        ...getCommonRoutes(), // Home
        ...navRoutes.slice(1) // Rest of navigation routes
      ];
    } else {
      routes = [...getCommonRoutes(), ...navRoutes];
    }

    console.log('Built routes from navigation:', routes);
    return routes;
  }

  // Navigation failed - log error
  console.error('CRITICAL: Enhanced navigation failed to load');
  console.error('Client:', client);
  console.error('User:', user.id, user.username);
  console.error('Permissions structure:', {
    hasPermissions: !!permissions,
    hasNavigation: !!permissions?.navigation,
    navigationVersion: permissions?.navigation?.version,
    navigationError: permissions?.navigation?.error
  });

  /**********************************************
   * APP_ADMIN FALLBACK
   * If user is APP_ADMIN, fall back to legacy client routes
   * This ensures admins can always access the system
   **********************************************/
  const isAppAdmin = role === 'APP_ADMIN' || user.groupRole === 'APP_ADMIN';

  console.log('[setUserRoutes] APP_ADMIN check:', {
    role,
    groupRole: user.groupRole,
    isAppAdmin
  });

  if (isAppAdmin) {
    console.warn('APP_ADMIN fallback: Enhanced navigation failed, using legacy routes');

    // Build routes with user profile first, then Home, then minimal routes
    routes = [
      {
        type: 'collapse',
        name: displayName,
        key: displayName,
        route: '/user/profile',
        icon: <InitialsAvatar user={user} size='sm' />,
        component: <UserProfile />,
        noCollapse: true,
      },
      ...getCommonRoutes(),
      { type: 'divider', key: 'divider-1' },
      {
        type: 'collapse',
        name: 'Dashboards',
        key: 'dashboards',
        icon: <Icon fontSize='medium'>dashboard</Icon>,
        collapse: [
          {
            name: "Home Base",
            key: 'home-base',
            route: '/dashboards/home',
            component: <Dashboard />
          }
        ]
      }
    ];

    console.log('[setUserRoutes] Base routes created, length:', routes.length);

    // Add client-specific routes based on client
    console.log('[setUserRoutes] Checking client-specific routes:', {
      client,
      permissionsNotNull: permissions !== null,
      willCallRdxRoutes: client === 'rdx' && permissions !== null
    });

    if (client === 'rdx' && permissions !== null) {
      console.log('[setUserRoutes] Calling rdxRoutes with role:', role);
      const clientRoutes = rdxRoutes(role, permissions);
      console.log('[setUserRoutes] rdxRoutes returned:', {
        routesCount: clientRoutes.length,
        routes: clientRoutes
      });
      routes = [...routes, ...clientRoutes];
      console.log('[setUserRoutes] Final routes after adding rdxRoutes:', {
        totalCount: routes.length,
        routes: routes
      });
    } else {
      console.log('[setUserRoutes] NOT calling rdxRoutes');
    }

    console.log('[setUserRoutes] Returning APP_ADMIN fallback routes');
    return routes;
  }

  // Return minimal error routes for non-admin users
  return [
    {
      type: 'collapse',
      name: displayName,
      key: displayName,
      route: '/user/profile',
      icon: <InitialsAvatar user={user} size='sm' />,
      component: <UserProfile />,
      noCollapse: true,
    },
    ...getCommonRoutes(),
    { type: 'divider', key: 'divider-error' },
    {
      type: 'collapse',
      name: '⚠️ Navigation Error',
      key: 'nav-error',
      icon: <Icon fontSize='medium'>error</Icon>,
      collapse: [
        {
          name: 'Contact Support',
          key: 'support',
          route: '/error/navigation',
          component: <div style={{ padding: '20px', color: 'red' }}>
            <h2>Navigation Failed to Load</h2>
            <p>Your navigation could not be generated. Please contact your administrator.</p>
            <p>Error: {permissions?.navigation?.error || 'Unknown navigation error'}</p>
          </div>
        }
      ]
    }
  ]
}